import chroma from 'chroma-js'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18n-lite'

import { MouseOverContainer } from '@yes.technology/react-toolkit'
import { HeightClass } from 'shared/types/toolkit'
import { ReactComponent as BrazilMapSvg } from '../../assets/br.svg'
import {
  getRegionsMapFormattedId,
  getTransformedMapData
} from '../../utils/regionsMapUtils'
import BrazilMiniMap from '../BrazilMiniMap'
import { DataChart } from '../ChartWrapper'
import useMapLayout from '../hooks/useMapLayout/useMapLayout'
import useMapRegionHover from '../hooks/useMapRegionHover/useMapRegionHover'
import useMapStateHover from '../hooks/useMapStateHover/useMapStateHover'
import useMapTooltipContent from '../hooks/useMapTooltipContent'
import { MouseOverContainerPosition, Title } from '../styles'
import { StyledBrazilChart } from './BrazilMap.styles'
import { BrazilMapProps, LegendData } from './BrazilMap.types'
import MapLegend from './MapLegend'
import * as utils from './utils'

function BrazilMap({
  chartModel,
  data,
  options,
  title,
  format = 'none',
  chartCustomization,
  mapLegend,
  displayNationalMinimap,
  expandedScale,
  customOffsets
}: BrazilMapProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const mapContainerRef = useRef<HTMLDivElement>(null)

  const { t } = useTranslation()

  const mapRenderingRef = useRef({ stateMap: false, regionMap: false })

  const {
    displayMode,
    defaultColor,
    colorAxis,
    tooltipHeightclass = 'extraSmall'
  } = options || {}

  const isRegionMap = displayMode === 'regions'
  const isStateMap = displayMode === 'states'

  const { language: locale } = useTranslation()

  const transformedData: DataChart = useMemo(() => {
    return getTransformedMapData(data)
  }, [data])

  const coloredData = useMemo(() => {
    const values = transformedData.slice(1).map((item) => Number(item[1]))
    const minValue = Math.min(...values)
    const maxValue = Math.max(...values)
    const colorScale = chroma
      .scale(colorAxis.colors)
      .domain([minValue, maxValue])
    return transformedData.slice(1).map(([region, value]) => ({
      region,
      value,
      color: value ? colorScale(Number(value)).hex() : defaultColor
    }))
  }, [colorAxis.colors, transformedData, defaultColor])

  const updateRegionChart = useCallback(() => {
    if (!isRegionMap) return

    utils.applyColors({
      coloredData,
      defaultColor,
      isRegionMap,
      isStateMap,
      svgElement: svgRef
    })

    utils.setLabels({
      labelSelector: '#regions_label',
      data: transformedData,
      svgElement: svgRef,
      coloredData,
      format,
      locale,
      idTransformer: (id: string) =>
        getRegionsMapFormattedId({
          id,
          defaultTranslatedLabels: {
            centroOeste: t('maps.regions.centroOeste'),
            nordeste: t('maps.regions.nordeste'),
            norte: t('maps.regions.norte'),
            sudeste: t('maps.regions.sudeste'),
            sul: t('maps.regions.sul'),
            nacional: t('maps.regions.nacional')
          },
          chartCustomization
        })
    })

    mapRenderingRef.current.regionMap = true
  }, [
    isRegionMap,
    coloredData,
    defaultColor,
    isStateMap,
    transformedData,
    format,
    locale,
    chartCustomization,
    t
  ])

  const updateStateChart = useCallback(() => {
    if (!isStateMap) return

    utils.applyColors({
      coloredData,
      defaultColor,
      isRegionMap,
      isStateMap,
      svgElement: svgRef
    })

    utils.setLabels({
      labelSelector: '#states_label',
      data: transformedData,
      svgElement: svgRef,
      coloredData,
      format,
      locale,
      idTransformer: (id: string) => id.replace('BR-', '')
    })

    mapRenderingRef.current.stateMap = true
  }, [
    isStateMap,
    coloredData,
    defaultColor,
    isRegionMap,
    transformedData,
    format,
    locale
  ])

  useEffect(() => {
    if (isRegionMap && !mapRenderingRef.current.regionMap) {
      updateRegionChart()
    } else if (isStateMap && !mapRenderingRef.current.stateMap) {
      updateStateChart()
    }
  }, [isRegionMap, isStateMap, updateRegionChart, updateStateChart])

  const sortedLegendData: LegendData[] = useMemo(() => {
    return coloredData
      .filter((item) => item.value !== null && item.value !== undefined)
      .map(({ value, ...rest }) => ({
        value: Number(value),
        ...rest
      }))
      .sort((a, b) => a.value - b.value)
  }, [coloredData])

  const tooltips = useMapTooltipContent({
    data: transformedData
  })

  const { tooltipCoordinates: regionTooltipCoordinates, regionKey } =
    useMapRegionHover({
      svgRef,
      containerRef: mapContainerRef,
      tooltips,
      customOffsets,
      enabled:
        isRegionMap &&
        mapRenderingRef.current.regionMap &&
        Object.keys(tooltips)?.length > 0
    })

  const { tooltipCoordinates: stateTooltipCoordinates, stateKey } =
    useMapStateHover({
      svgRef,
      containerRef: mapContainerRef,
      tooltips,
      customOffsets,
      enabled:
        isStateMap &&
        mapRenderingRef.current.stateMap &&
        Object.keys(tooltips)?.length > 0
    })

  const tooltipCoordinates = isStateMap
    ? stateTooltipCoordinates
    : regionTooltipCoordinates

  const hoverKey = isStateMap ? stateKey : regionKey

  const { isColumnLayout } = useMapLayout(mapContainerRef)

  return (
    <StyledBrazilChart
      ref={mapContainerRef}
      $isColumnLayout={isColumnLayout}
      {...{ chartModel }}
    >
      {title && <Title>{title}</Title>}
      {!!hoverKey && !!tooltips[hoverKey]?.html && (
        <MouseOverContainerPosition
          $top={tooltipCoordinates?.y ?? '0px'}
          $left={tooltipCoordinates?.x ?? '0px'}
        >
          <MouseOverContainer
            title={tooltips[hoverKey]?.title || ''}
            description={tooltips[hoverKey]?.html || ''}
            heightClass={tooltipHeightclass as HeightClass}
            connectorHeight={12}
            connectorPosition={tooltipCoordinates?.placement}
          />
        </MouseOverContainerPosition>
      )}
      <div style={{ position: 'relative' }}>
        <BrazilMapSvg width={'100%'} ref={svgRef} />
        {mapLegend?.title && (
          <MapLegend
            title={mapLegend.title}
            data={sortedLegendData}
            format={format}
          />
        )}
      </div>
      {displayNationalMinimap && (
        <BrazilMiniMap
          data={tooltips?.['nacional']}
          color={options?.miniMapColor}
          expandedScale={expandedScale}
          tooltipHeightclass={tooltipHeightclass as HeightClass}
          isColumnLayout={isColumnLayout}
        />
      )}
    </StyledBrazilChart>
  )
}

export default BrazilMap
